export const WORKSPACE_METADATA_SCHEMA_VERSION = 3;

export type WorkspaceMetadataVisibilityMode =
  | 'user_effective'
  | 'workspace_admin'
  | 'system_audit';

export type WorkspaceListMetadataKind =
  | 'doc'
  | 'journal'
  | 'template'
  | 'folder'
  | 'area'
  | 'trash-folder';

export type WorkspaceMetadataPermissionHint =
  | 'read'
  | 'comment'
  | 'write'
  | 'manage'
  | 'unknown';

export type WorkspaceMetadataSyncState =
  | 'local'
  | 'pending'
  | 'revalidating'
  | 'fresh'
  | 'stale'
  | 'conflict';

export interface WorkspaceListMetadata {
  workspaceId: string;
  nodeId: string;
  kind: WorkspaceListMetadataKind;
  title: string;
  parentId: string | null;
  areaId: string | null;
  sortKey: string;
  hasChildren: boolean;
  childCount: number | null;
  subtreeRevision: string | null;
  trashedAt: string | null;
  trashedBy?: string | null;
  createdAt?: string;
  updatedAt: string;
  permissionHint: WorkspaceMetadataPermissionHint;
  syncState: WorkspaceMetadataSyncState;
  isDemo?: boolean;
  docIds?: string[];
  parentTrashed?: boolean;
  folder?: WorkspaceMetadataItem['folder'];
}

export interface WorkspaceMetadataShapeState {
  accountId: string;
  principalId: string;
  serverId: string;
  serverBaseUrl: string;
  visibilityMode: WorkspaceMetadataVisibilityMode;
  permissionGraphVersion: number | null;
  schemaVersion: number;
  workspaceId: string;
  tenantId: string;
  shapeKey: string;
  token: string | null;
  cursor: string | null;
  pageCursors: Record<string, string | null>;
  lastSuccessfulSyncAt: string | null;
}

export interface WorkspaceMetadataPendingMutation {
  accountId: string;
  principalId: string;
  serverId: string;
  serverBaseUrl: string;
  visibilityMode: WorkspaceMetadataVisibilityMode;
  tenantId: string;
  shapeKey: string;
  schemaVersion: number;
  permissionGraphVersion: number | null;
  localMutationId: string;
  workspaceId: string;
  nodeId: string;
  op:
    | 'create'
    | 'move'
    | 'rename'
    | 'trash'
    | 'restore'
    | 'delete'
    | 'permission_hint';
  baseRevision: string | null;
  patch: Partial<WorkspaceListMetadata>;
  status: 'queued' | 'syncing' | 'acked' | 'failed' | 'conflict';
  createdAt: string;
}

export interface WorkspaceMetadataItem {
  id: string;
  type: 'doc' | 'folder';
  parentId?: string | null;
  title: string;
  trashed: boolean;
  trashedAt?: string | null;
  trashedBy?: string | null;
  createdAt: string;
  updatedAt: string;
  revision?: string;
  permissionGraphVersion?: number;
  parentTrashed?: boolean;
  isDemo?: boolean;
  hasChildren?: boolean;
  childCount?: number | null;
  subtreeRevision?: string | null;
  folder?: {
    ownerUserId: string | null;
    systemKind: string | null;
    visibility: string;
    inheritPermissions: boolean;
    isWorkspaceRoot: boolean;
    immutable: boolean;
    storageLimitBytes: number | null;
    storageUsedBytes: number | null;
    metadataOnly: boolean;
  };
}

export interface WorkspaceMetadataPage {
  items: WorkspaceMetadataItem[];
  pageInfo: {
    hasNextPage: boolean;
    endCursor: string | null;
  };
  workspaceRevision: number;
  permissionGraphVersion: number;
}

export interface WorkspaceFolderChildrenPage extends WorkspaceMetadataPage {
  parent: WorkspaceMetadataItem | null;
}

export interface WorkspaceMetadataChangeEntry {
  changeId: string;
  workspaceId?: string;
  resourceType: 'doc' | 'folder' | 'workspace';
  resourceId: string | null;
  changeType: string;
  occurredAt?: string;
  source?: string;
}

export interface WorkspaceMetadataDeltaPage {
  items: WorkspaceMetadataItem[];
  changes: WorkspaceMetadataChangeEntry[];
  nextToken: string | null;
  workspaceRevision: number;
  permissionGraphVersion: number;
  hasMore: boolean;
  resetRequired: boolean;
  resetReason: 'invalid_token' | 'retention_gap' | 'visibility_changed' | null;
}

export interface WorkspaceMetadataDeltaHead {
  token: string;
}
